import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Lightbox } from '@/components/Lightbox';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft, Calendar, Car, User, Tag, Gauge, Shield, Edit, Trash2,
  Send, MessageCircle, Loader2, ChevronLeft, ChevronRight, Images,
  Share2, CheckCircle, Ban, ExternalLink, MapPin, Eye, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ListingDetailPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [currentImage, setCurrentImage] = useState(0);
  const [showShare, setShowShare] = useState(false);
  const [showEmailShare, setShowEmailShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMsg, setShareMsg] = useState('');
  const [sharingEmail, setSharingEmail] = useState(false);
  const msgEndRef = useRef();
  const getToken = () => localStorage.getItem('shrt_token');
  const currentUserId = user?.id || '';

  useEffect(() => { fetchListing(); }, [listingId]);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setListing(res.data);
    } catch { toast.error('Inserat nicht gefunden'); navigate('/marketplace'); }
    finally { setLoading(false); }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/listings/${listingId}/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMessages(res.data.messages || []);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Fehler beim Laden der Nachrichten'); }
  };

  const openMessages = () => { setShowMessages(true); fetchMessages(); };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/marketplace/listings/${listingId}/messages`, { text: newMsg }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setNewMsg('');
      fetchMessages();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Senden'); }
    finally { setSending(false); }
  };

  const deleteListing = async () => {
    if (!window.confirm('Inserat wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/api/marketplace/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Inserat gelöscht');
      navigate('/marketplace');
    } catch { toast.error('Fehler'); }
  };

  const toggleSold = async () => {
    const newStatus = listing.status === 'sold' ? 'active' : 'sold';
    try {
      await axios.patch(`${API_URL}/api/marketplace/listings/${listingId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setListing(prev => ({ ...prev, status: newStatus }));
      toast.success(newStatus === 'sold' ? 'Als verkauft markiert' : 'Wieder aktiv');
    } catch { toast.error('Fehler'); }
  };

  const toggleSuspend = async () => {
    const newStatus = listing.status === 'suspended' ? 'active' : 'suspended';
    try {
      await axios.patch(`${API_URL}/api/marketplace/admin/listings/${listingId}/suspend`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setListing(prev => ({ ...prev, status: newStatus }));
      toast.success(newStatus === 'suspended' ? 'Inserat gesperrt' : 'Inserat freigegeben');
    } catch { toast.error('Fehler'); }
  };

  const shareUrl = `${window.location.origin}/public/marketplace/${listingId}`;
  const shareText = `${listing?.title} – S.H.R.T. Marktplatz`;
  const shareWhatsApp = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link kopiert');
    setShowShare(false);
  };

  const shareViaEmail = async () => {
    if (!shareEmail.trim() || !shareEmail.includes('@')) {
      toast.error('Bitte gültige E-Mail-Adresse eingeben');
      return;
    }
    setSharingEmail(true);
    try {
      await axios.post(`${API_URL}/api/marketplace/listings/${listingId}/share`,
        { email: shareEmail, message: shareMsg },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Inserat an ${shareEmail} geteilt`);
      setShowEmailShare(false);
      setShowShare(false);
      setShareEmail('');
      setShareMsg('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally { setSharingEmail(false); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!listing) return null;

  const isOwner = listing.member_id === currentUserId;
  const isSell = listing.type === 'sell';
  const isSold = listing.status === 'sold';
  const isSuspended = listing.status === 'suspended';
  const imgs = listing.images || [];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Status banners */}
        {isSold && (
          <div className="bg-green-500/10 border border-green-500/30 text-green-700 p-3 mb-4 flex items-center gap-2 text-sm font-semibold" data-testid="sold-banner">
            <CheckCircle size={16} /> Dieses Fahrzeug wurde verkauft
          </div>
        )}
        {isSuspended && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-600 p-3 mb-4 flex items-center gap-2 text-sm font-semibold" data-testid="suspended-banner">
            <Ban size={16} /> Dieses Inserat wurde von einem Administrator gesperrt
          </div>
        )}

        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <button onClick={() => navigate('/marketplace')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Marktplatz
          </button>
          <div className="flex gap-2 flex-wrap">
            {/* Share button */}
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowShare(!showShare)} className="rounded-none" data-testid="share-btn">
                <Share2 size={14} className="mr-1" /> Teilen
              </Button>
              {showShare && (
                <div className="absolute right-0 top-full mt-1 bg-card border border-border shadow-lg z-20 p-2 min-w-[200px]">
                  <a href={shareWhatsApp} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 text-sm hover:bg-muted/50 transition-colors" data-testid="share-whatsapp">
                    WhatsApp
                  </a>
                  <button onClick={copyLink} className="block w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors" data-testid="share-copy-link">
                    Link kopieren
                  </button>
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 text-sm hover:bg-muted/50 transition-colors" data-testid="share-open-public">
                    <ExternalLink size={12} className="inline mr-1" /> Öffentliche Seite
                  </a>
                  <button onClick={() => { setShowEmailShare(true); setShowShare(false); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors" data-testid="share-email-btn">
                    <Mail size={12} className="inline mr-1" /> Per E-Mail teilen
                  </button>
                </div>
              )}
            </div>
            {/* Owner actions */}
            {isOwner && (
              <>
                {isSell && !isSuspended && (
                  <Button variant="outline" size="sm" onClick={toggleSold} className={`rounded-none ${isSold ? 'text-green-600 border-green-300' : ''}`} data-testid="toggle-sold-btn">
                    <CheckCircle size={14} className="mr-1" /> {isSold ? 'Wieder aktivieren' : 'Verkauft'}
                  </Button>
                )}
                {!isSold && !isSuspended && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/marketplace/${listingId}/edit`)} className="rounded-none" data-testid="edit-listing-btn">
                    <Edit size={14} className="mr-1" /> Bearbeiten
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={deleteListing} className="rounded-none text-red-500 hover:text-red-600" data-testid="delete-listing-btn">
                  <Trash2 size={14} className="mr-1" /> Löschen
                </Button>
              </>
            )}
            {/* Admin suspend */}
            {isAdmin && !isOwner && (
              <Button variant="outline" size="sm" onClick={toggleSuspend} className={`rounded-none ${isSuspended ? 'text-green-600' : 'text-red-500'}`} data-testid="admin-suspend-btn">
                <Ban size={14} className="mr-1" /> {isSuspended ? 'Freigeben' : 'Sperren'}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Images */}
          <div className="lg:col-span-3">
            {imgs.length > 0 ? (
              <div>
                <div className="relative aspect-[4/3] overflow-hidden border border-border cursor-pointer" onClick={() => setLightboxIdx(currentImage)}>
                  <img src={imgs[currentImage]?.url} alt={listing.title} className="w-full h-full object-cover" />
                  <span className={`absolute top-3 left-3 text-xs font-bold uppercase tracking-wider px-2 py-1 ${isSold ? 'bg-green-600 text-white' : isSell ? 'bg-primary text-white' : 'bg-blue-500 text-white'}`}>
                    {isSold ? 'Verkauft' : isSell ? 'Verkauf' : 'Suche'}
                  </span>
                  {imgs.length > 1 && (
                    <span className="absolute top-3 right-3 text-xs text-white bg-black/60 px-2 py-1 flex items-center gap-1">
                      <Images size={12} /> {currentImage + 1}/{imgs.length}
                    </span>
                  )}
                </div>
                {imgs.length > 1 && (
                  <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                    {imgs.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        className={`flex-shrink-0 w-16 h-16 overflow-hidden border-2 transition-colors ${i === currentImage ? 'border-primary' : 'border-border opacity-60 hover:opacity-100'}`}
                      >
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-[4/3] bg-muted/30 border border-border flex items-center justify-center">
                <Car className="w-20 h-20 text-muted-foreground/20" />
              </div>
            )}
          </div>

          {/* Right: Details */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-card border border-border p-5">
              <h1 className="font-['Oswald'] text-2xl font-bold uppercase tracking-wide" data-testid="listing-title">{listing.title}</h1>
              
              <div className="mt-4 space-y-2">
                {listing.brand && <div className="flex items-center gap-2 text-sm"><Car size={14} className="text-muted-foreground" /><span>{listing.brand} {listing.model}</span></div>}
                {isSell && listing.year && <div className="flex items-center gap-2 text-sm"><Calendar size={14} className="text-muted-foreground" /><span>Baujahr {listing.year}</span></div>}
                {!isSell && listing.year_from && <div className="flex items-center gap-2 text-sm"><Calendar size={14} className="text-muted-foreground" /><span>Baujahr {listing.year_from}–{listing.year_to || '...'}</span></div>}
                {listing.mileage && <div className="flex items-center gap-2 text-sm"><Gauge size={14} className="text-muted-foreground" /><span>{listing.mileage}</span></div>}
                {listing.condition && <div className="flex items-center gap-2 text-sm"><Shield size={14} className="text-muted-foreground" /><span>{listing.condition}</span></div>}
                {listing.location && <div className="flex items-center gap-2 text-sm"><MapPin size={14} className="text-muted-foreground" /><span>{listing.location}</span></div>}
                {listing.viewing_possible && <div className="flex items-center gap-2 text-sm"><Eye size={14} className="text-green-500" /><span className="text-green-600">Besichtigung möglich</span></div>}
                {listing.external_link && (
                  <a href={listing.external_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid="external-link">
                    <ExternalLink size={14} /><span>Externes Inserat ansehen</span>
                  </a>
                )}
              </div>

              {/* Price */}
              <div className={`mt-4 p-3 text-center border ${isSold ? 'border-green-500/30 bg-green-500/5' : isSell ? 'border-primary/30 bg-primary/5' : 'border-blue-500/30 bg-blue-500/5'}`}>
                <span className={`text-xl font-bold ${isSold ? 'text-green-600 line-through' : isSell ? 'text-primary' : 'text-blue-500'}`}>
                  {isSell ? (
                    listing.price_on_request || listing.price == null ? 'Preis auf Anfrage' : `CHF ${listing.price.toLocaleString('de-CH')}`
                  ) : (
                    listing.budget_max ? `Budget: CHF ${listing.budget_max.toLocaleString('de-CH')}` : 'Budget offen'
                  )}
                </span>
                {isSold && <p className="text-green-600 text-sm font-semibold mt-1">Verkauft</p>}
              </div>
            </div>

            {/* Seller info */}
            <div className="bg-card border border-border p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <Link to={`/members/${listing.member_id}`} className="font-semibold hover:text-primary hover:underline transition-colors" data-testid="seller-profile-link">
                    {listing.member_name}
                  </Link>
                  <p className="text-xs text-muted-foreground">Erstellt am {new Date(listing.created_at).toLocaleDateString('de-CH')}</p>
                </div>
              </div>
              {!isOwner && !isSold && !isSuspended && (
                <Button onClick={openMessages} className="w-full mt-4 rounded-none uppercase tracking-wider" data-testid="contact-btn">
                  <MessageCircle className="w-4 h-4 mr-2" /> Nachricht senden
                </Button>
              )}
              {isOwner && listing.message_count > 0 && (
                <Button onClick={openMessages} variant="outline" className="w-full mt-4 rounded-none" data-testid="view-messages-btn">
                  <MessageCircle className="w-4 h-4 mr-2" /> {listing.message_count} Nachrichten
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {listing.description && (
          <div className="bg-card border border-border p-5 mt-6">
            <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide mb-3">Beschreibung</h2>
            <p className="text-sm whitespace-pre-wrap">{listing.description}</p>
          </div>
        )}

        {/* Messages Panel */}
        {showMessages && (
          <div className="bg-card border border-border mt-6">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide flex items-center gap-2">
                <MessageCircle className="text-primary" size={18} /> Nachrichten
              </h2>
              <button onClick={() => setShowMessages(false)} className="text-muted-foreground hover:text-foreground text-sm">Schliessen</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-3" data-testid="messages-container">
              {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Noch keine Nachrichten. Schreibe die erste!</p>}
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] p-3 text-sm ${m.sender_id === currentUserId ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50 border border-border'}`}>
                    <p className="text-xs font-semibold mb-1">{m.sender_name}</p>
                    <p>{m.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(m.created_at).toLocaleString('de-CH')}</p>
                  </div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>
            <div className="p-4 border-t border-border flex gap-2">
              <Input
                value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Nachricht schreiben..." className="rounded-none flex-1"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                data-testid="message-input"
              />
              <Button onClick={sendMessage} disabled={sending || !newMsg.trim()} className="rounded-none" data-testid="send-message-btn">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          </div>
        )}
      </div>

      {lightboxIdx >= 0 && (
        <Lightbox
          images={imgs.map(i => ({ src: i.url, alt: listing.title }))}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
        />
      )}

      {/* Email share modal */}
      {showEmailShare && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEmailShare(false)}>
          <div className="bg-card border border-border p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide mb-4">Inserat per E-Mail teilen</h3>
            <p className="text-sm text-muted-foreground mb-4">«{listing.title}» wird per E-Mail an den Empfänger gesendet.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">E-Mail-Adresse *</label>
                <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="empfaenger@beispiel.ch" className="rounded-none" data-testid="share-email-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Persönliche Nachricht (optional)</label>
                <textarea value={shareMsg} onChange={e => setShareMsg(e.target.value)} placeholder="z.B. Schau dir dieses Inserat an..." className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[60px] resize-y" data-testid="share-email-message" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowEmailShare(false)} className="rounded-none flex-1">Abbrechen</Button>
              <Button onClick={shareViaEmail} disabled={sharingEmail || !shareEmail.trim()} className="rounded-none flex-1" data-testid="share-email-send-btn">
                {sharingEmail ? <Loader2 size={16} className="animate-spin mr-2" /> : <Mail size={16} className="mr-2" />}
                Senden
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
